import { useState, useCallback, type FormEvent, useEffect } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useAuth } from '@/contexts/AuthContext';

interface LinkedCompany {
  id: number;
  companyName: string;
}

interface CustomerDoc {
  id: number;
  docType: string;
  fileName: string;
}

interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  idType: string | null;
  idNumber: string | null;
  nationality: string | null;
  occupation: string | null;
  customerType: string;
  status: string;
  riskLevel: string;
  notes: string | null;
  companyId?: number | null;
  linkedCompanies?: LinkedCompany[];
  documents?: CustomerDoc[];
}

interface EditCustomerModalProps {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CUSTOMER_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'llp', label: 'LLP' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'trust', label: 'Trust' },
  { value: 'ngo', label: 'NGO' },
  { value: 'others', label: 'Others' },
];

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

export default function EditCustomerModal({ open, customer, onClose, onSuccess }: EditCustomerModalProps) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Basic info
  const [customerType, setCustomerType] = useState('individual');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('active');
  const [riskLevel, setRiskLevel] = useState('low');
  const [companyId, setCompanyId] = useState<string>('');
  const [managedBy, setManagedBy] = useState<string>('');

  // Identity
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [nationality, setNationality] = useState('');
  const [occupation, setOccupation] = useState('');
  const [notes, setNotes] = useState('');

  // Linked companies
  const [companyCount, setCompanyCount] = useState(1);
  const [companyNames, setCompanyNames] = useState<string[]>(['']);

  const isIndividual = customerType === 'individual';

  // Fetch companies for superadmin dropdown
  const { data: companiesData } = useApiQuery<{ companies?: { id: number; name: string }[] }>({
    endpoint: '/companies',
    enabled: open && isSuperAdmin,
  });
  const companiesList = companiesData?.companies || [];

  // Fetch users for managed_by dropdown
  const { data: usersData } = useApiQuery<{ data?: { id: number; full_name: string }[] } | { id: number; full_name: string }[]>({
    endpoint: '/users',
    enabled: open,
  });
  const usersList = Array.isArray(usersData)
    ? usersData
    : (usersData as { data?: { id: number; full_name: string }[] } | null)?.data || [];

  useEffect(() => {
    if (open && customer) {
      setCustomerType(customer.customerType || 'individual');
      setFirstName(customer.firstName || '');
      setLastName(customer.lastName || '');
      setEmail(customer.email || '');
      setPhone(customer.phone || '');
      setStatus(customer.status || 'active');
      setRiskLevel(customer.riskLevel || 'low');
      setCompanyId(customer.companyId ? String(customer.companyId) : '');
      setManagedBy(customer.managedBy ? String(customer.managedBy) : '');
      setDateOfBirth(customer.dateOfBirth ? customer.dateOfBirth.split('T')[0] : '');
      setGender(customer.gender || '');
      setIdType(customer.idType || '');
      setIdNumber(customer.idNumber || '');
      setAddress(customer.address || '');
      setCity(customer.city || '');
      setState(customer.state || '');
      setCountry(customer.country || '');
      setPostalCode(customer.postalCode || '');
      setNationality(customer.nationality || '');
      setOccupation(customer.occupation || '');
      setNotes(customer.notes || '');

      const linked = customer.linkedCompanies || [];
      const count = Math.max(linked.length, 1);
      setCompanyCount(count);
      setCompanyNames(
        Array.from({ length: count }, (_, i) => linked[i]?.companyName || '')
      );

      setStep(1);
      setError(null);
      setLoading(false);
    }
  }, [open, customer]);

  const handleClose = useCallback(() => {
    setStep(1);
    setError(null);
    onClose();
  }, [onClose]);

  const handleCompanyCountChange = (count: number) => {
    setCompanyCount(count);
    setCompanyNames((prev) =>
      Array.from({ length: count }, (_, i) => prev[i] || '')
    );
  };

  const updateCompanyName = (index: number, value: string) => {
    setCompanyNames((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!customer) return;
    setError(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('First name, last name, and email are required');
      setStep(1);
      return;
    }

    if (isSuperAdmin && !companyId && !isIndividual) {
      setError('Company is required for non-individual customers');
      setStep(1);
      return;
    }

    if (isIndividual && dateOfBirth) {
      const birth = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      if (age < 18) {
        setError('Individual customers must be 18 years or older.');
        setStep(2);
        return;
      }
    }

    if (!isIndividual) {
      const validNames = companyNames.filter((n) => n.trim()).length;
      if (validNames < companyCount) {
        setError(`Please enter names for all ${companyCount} linked companies.`);
        setStep(3);
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('ekyc_token');
      const body: Record<string, unknown> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        customerType,
        status,
        riskLevel,
        phone: phone.trim() || null,
        dateOfBirth: dateOfBirth || null,
        gender: gender || null,
        address: address.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        country: country.trim() || null,
        postalCode: postalCode.trim() || null,
        idType: idType || null,
        idNumber: idNumber.trim() || null,
        nationality: nationality.trim() || null,
        occupation: occupation.trim() || null,
        notes: notes.trim() || null,
        managedBy: managedBy ? Number(managedBy) : null,
      };

      if (isSuperAdmin && companyId) {
        body.companyId = Number(companyId);
      }

      if (!isIndividual) {
        body.linkedCompanies = companyNames.filter((n) => n.trim());
      }

      const res = await fetch(`${API_BASE}/customers/${customer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.message || 'Failed to update customer');
      }

      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer');
      setLoading(false);
    }
  };

  if (!open || !customer) return null;

  const renderStepIndicator = () => (
    <div className="flex items-center gap-2 mb-6">
      {[1, 2, 3].map((s) => (
        <div key={s} className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              s === step
                ? 'bg-brand-600 text-white'
                : s < step
                  ? 'bg-brand-100 text-brand-700'
                  : 'bg-stone-100 text-stone-400'
            }`}
          >
            {s < step ? <i className="ri-check-line" /> : s}
          </div>
          <span className={`text-xs font-medium ${s === step ? 'text-brand-700' : 'text-stone-400'}`}>
            {s === 1 ? 'Basic Info' : s === 2 ? 'Identity' : 'Companies'}
          </span>
          {s < 3 && <div className={`w-8 h-px ${s < step ? 'bg-brand-300' : 'bg-stone-200'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h2 className="text-lg font-bold text-stone-900">Edit Customer</h2>
            <p className="text-stone-400 text-xs mt-0.5">
              Step {step} of 3 &mdash; {step === 1 ? 'Basic Information' : step === 2 ? 'Identity Details' : 'Linked Companies'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {renderStepIndicator()}

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2 mb-4">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          {/* STEP 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Customer Type *</label>
                <select
                  value={customerType}
                  onChange={(e) => setCustomerType(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  {CUSTOMER_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {isSuperAdmin && (
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Company</label>
                  <select
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="">Keep current / optional</option>
                    {companiesList.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <p className="text-stone-400 text-[10px] mt-1">
                    <i className="ri-information-line mr-0.5" />
                    Changing company moves this customer to a different tenant. Only superadmins can do this.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Managed By</label>
                <select
                  value={managedBy}
                  onChange={(e) => setManagedBy(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Unassigned</option>
                  {usersList.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name || `User #${u.id}`}</option>
                  ))}
                </select>
                <p className="text-stone-400 text-[10px] mt-1">
                  <i className="ri-information-line mr-0.5" />
                  Change who manages this customer profile.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">First Name *</label>
                  <input
                    required
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Last Name *</label>
                  <input
                    required
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Email *</label>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="under_review">Under Review</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Risk Level</label>
                  <select
                    value={riskLevel}
                    onChange={(e) => setRiskLevel(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 resize-none"
                />
                <p className="text-stone-400 text-[10px] text-right mt-0.5">{notes.length}/500</p>
              </div>
            </div>
          )}

          {/* STEP 2: Identity */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">
                    Date of Birth {isIndividual && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    required={isIndividual}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                  {isIndividual && dateOfBirth && (
                    <p className="text-xs mt-1">
                      {(() => {
                        const birth = new Date(dateOfBirth);
                        const today = new Date();
                        let a = today.getFullYear() - birth.getFullYear();
                        const m = today.getMonth() - birth.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) a--;
                        return (
                          <span className={a >= 18 ? 'text-emerald-600' : 'text-red-500'}>
                            Age: {a} {a >= 18 ? '(Eligible)' : '(Must be 18+)'}
                          </span>
                        );
                      })()}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Gender</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">ID Type</label>
                  <select
                    value={idType}
                    onChange={(e) => setIdType(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                  >
                    <option value="">Select ID type</option>
                    <option value="Aadhaar">Aadhaar</option>
                    <option value="PAN">PAN</option>
                    <option value="Passport">Passport</option>
                    <option value="Driving License">Driving License</option>
                    <option value="Voter ID">Voter ID</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">ID Number</label>
                  <input
                    type="text"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Address</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">State</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Country</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Nationality</label>
                  <input
                    type="text"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Occupation</label>
                  <input
                    type="text"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Companies (only for non-individual) */}
          {step === 3 && (
            <div className="space-y-5">
              {isIndividual ? (
                <div className="text-center py-8">
                  <i className="ri-building-4-line text-stone-300 text-3xl mb-3 w-12 h-12 flex items-center justify-center mx-auto" />
                  <p className="text-stone-500 text-sm">Linked companies are not applicable for individual customers.</p>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="mt-3 text-brand-600 text-sm hover:underline"
                  >
                    Go back to Identity
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Number of Linked Companies</label>
                    <select
                      value={companyCount}
                      onChange={(e) => handleCompanyCountChange(Number(e.target.value))}
                      className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                    >
                      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>

                  {Array.from({ length: companyCount }).map((_, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={companyNames[idx] || ''}
                        onChange={(e) => updateCompanyName(idx, e.target.value)}
                        placeholder={`Company ${idx + 1} name`}
                        required={!isIndividual}
                        className="flex-1 border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="pt-4 mt-4 border-t border-stone-100 flex items-center justify-between">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (step === 1 && (!firstName.trim() || !lastName.trim() || !email.trim())) {
                      setError('First name, last name, and email are required');
                      return;
                    }
                    if (step === 1 && isSuperAdmin && !companyId && !isIndividual) {
                      setError('Company is required for non-individual customers');
                      return;
                    }
                    setError(null);
                    setStep(step + 1);
                  }}
                  className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors whitespace-nowrap"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}